import { useState } from 'react';
import type { ViewBody } from '@ayumia/contracts';
import { collectReferences, evaluateViewCondition, resolveActionInputs } from '@ayumia/component-runtime';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { CommandDialog } from './CommandDialog';
import type { LineDraft } from './LinesTab';
import { useAppServices, type ViewCommand } from './ViewContext';

/**
 * Actions d'une vue (ADR-048 §8, ADR-064 §3) : chacune est une commande nommée, l'ouverture d'une
 * vue ou le démarrage d'un workflow. Sa `condition` ne fait que l'afficher ou non : la commande est
 * ré-autorisée au serveur à l'exécution (ADR-023), et un refus s'affiche comme tel.
 *
 * Une commande vise l'enregistrement affiché, ou la cible que l'action désigne (`target`) ; une
 * commande de création ne vise rien. Sa charge utile part des valeurs pré-remplies (`inputs`) ; si
 * la définition déclarée a d'autres champs, un Dialog les fait saisir — le navigateur ne devine
 * jamais une charge utile.
 *
 * Une action peut se montrer grisée avec sa raison au lieu d'être cachée (`unavailable_when`, ADR-133
 * §4) : le refus se voit d'avance. Avec `requires_complete`, sa charge utile vient de la décision
 * ligne par ligne d'un onglet `lines`, et elle reste grisée tant qu'une ligne est sans décision.
 * Présentation dans les deux cas : le serveur réévalue.
 *
 * Un sujet déclaratif (`subject`, view-body 3.5.0, ADR-136 addendum du 2026-10-16) devient
 * `subject: { type, id }` dans la charge utile (feuille de comptage par `GenerateDocument`) ; tant qu'il
 * ne se résout pas, l'action reste grisée avec sa raison. Quand la commande rend un fichier
 * (`result.file_id`), son téléchargement s'ouvre, sous les droits de lecture de qui l'a demandé. Une
 * action à sujet ne vise pas l'enregistrement affiché, sauf `target` explicite.
 */

type ViewAction = NonNullable<ViewBody['actions']>[number];

export interface RecordTarget {
  type: string;
  id: string;
  version?: number;
}

interface PendingCommand {
  command: ViewCommand;
  prefilled: Record<string, unknown>;
  target: { type: string; id: string } | null;
  expectedVersion?: number;
  subject?: { type: string; id: string };
}

export function ActionBar({ actions, record, target, onDone, drafts }: { actions: readonly ViewAction[]; record: Record<string, unknown> | null; target: RecordTarget | null; onDone: () => void; drafts?: ReadonlyMap<string, LineDraft> }) {
  const services = useAppServices();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<{ category: string; message: string } | null>(null);
  const [pending, setPending] = useState<PendingCommand | null>(null);

  // `view-body` ne porte pas (encore) de portée d'action : une action dont la condition lit
  // `$record`, ou non principale, vise un enregistrement ; une action principale sans `$record`
  // vise la collection (création). Présentation seulement : le serveur tranche.
  const recordScoped = (action: ViewAction): boolean => action.primary !== true || collectReferences(action.condition ?? null).some((reference) => reference.root === 'record');
  const relevant = actions
    .filter((action) => recordScoped(action) === (target !== null))
    .filter((action) => evaluateViewCondition(action.condition, { record, context: services.context }));
  if (relevant.length === 0) return null;

  const scope = { record, context: services.context };
  /** Sujet déclaré de l'action, résolu sur l'enregistrement et le contexte ; `null` s'il ne se résout pas. */
  const subjectOf = (action: ViewAction): { type: string; id: string } | null => {
    if (action.subject === undefined) return null;
    const id = resolveActionInputs({ id: action.subject.id }, scope)['id'];
    return typeof id === 'string' && id !== '' ? { type: action.subject.object_type, id } : null;
  };
  /** Raison pour laquelle l'action est grisée, ou `null` : refus visible d'avance, ou décisions incomplètes. */
  const unavailable = (action: ViewAction): string | null => {
    if (action.subject !== undefined && subjectOf(action) === null) return 'Le sujet de cette action est introuvable sur l’enregistrement.';
    if (action.unavailable_when !== undefined && evaluateViewCondition(action.unavailable_when.condition, scope)) return text(action.unavailable_when.reason);
    if (action.requires_complete !== undefined) {
      const draft = drafts?.get(action.requires_complete);
      if (draft === undefined) return 'Les lignes à décider sont introuvables sur cet enregistrement.';
      if (draft.missing > 0) return `${draft.missing} ligne(s) sans décision : chacune se décide avant.`;
    }
    return null;
  };

  const prepare = (action: ViewAction): PendingCommand | null => {
    const declared = services.commands?.get(action.command_name!);
    const prefilled = resolveActionInputs(action.inputs as Record<string, unknown> | undefined, scope);
    if (action.subject !== undefined) {
      const declaredSubject = subjectOf(action);
      if (declaredSubject === null) return null;
      prefilled['subject'] = declaredSubject;
    }
    if (action.requires_complete !== undefined) {
      const draft = drafts?.get(action.requires_complete);
      if (draft !== undefined) prefilled[action.requires_complete] = draft.entries;
    }
    let commandTarget: { type: string; id: string } | null = null;
    let expectedVersion: number | undefined;
    if (action.target !== undefined) {
      const id = resolveActionInputs({ id: action.target.id }, scope)['id'];
      if (typeof id !== 'string') return null;
      commandTarget = { type: action.target.object_type, id };
    } else if (target !== null && declared?.effect !== 'create' && action.subject === undefined) {
      // Une action à sujet déclaré (GenerateDocument) ne modifie pas l'enregistrement affiché : pas de cible implicite.
      commandTarget = { type: target.type, id: target.id };
      expectedVersion = target.version;
    }
    const command: ViewCommand = declared ?? { command_name: action.command_name!, object_type: target?.type ?? '', label: action.label, payload: [] };
    const out: PendingCommand = { command, prefilled, target: commandTarget };
    if (expectedVersion !== undefined) out.expectedVersion = expectedVersion;
    const subject = commandTarget ?? (target === null ? null : { type: target.type, id: target.id });
    if (subject !== null) out.subject = subject;
    return out;
  };

  /** Ouvre le fichier rendu par la commande ; un refus de lecture se dit, le document reste généré. */
  const openDownload = async (fileId: string): Promise<void> => {
    try {
      const response = await services.client.query<{ download: { url: string } }>('GetFileDownload', { file_id: fileId });
      window.open(response.data.download.url, '_blank', 'noopener');
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : String(caught);
      setError({ category: 'POLICY_REJECTION', message: `le document est généré, mais son téléchargement est refusé (${message}).` });
    }
  };

  const run = async (action: ViewAction): Promise<void> => {
    setError(null);
    if (action.kind === 'open_view' && action.view_ref !== undefined) {
      services.navigate(target === null ? { kind: 'view', view_ref: action.view_ref } : { kind: 'view', view_ref: action.view_ref, record_id: target.id });
      return;
    }
    if (action.kind === 'command' && action.command_name !== undefined) {
      const prepared = prepare(action);
      if (prepared === null) {
        setError({ category: 'VALIDATION_ERROR', message: 'La cible de cette action est introuvable sur l’enregistrement.' });
        return;
      }
      if (prepared.command.payload.some((field) => !Object.hasOwn(prepared.prefilled, field.field_id))) {
        setPending(prepared);
        return;
      }
    }
    setBusy(action.action_id);
    try {
      if (action.kind === 'command' && action.command_name !== undefined) {
        const prepared = prepare(action)!;
        const input: Parameters<typeof services.client.command>[1] = { payload: prepared.prefilled };
        if (prepared.target !== null) input.target = prepared.target;
        if (prepared.expectedVersion !== undefined) input.expected_version = prepared.expectedVersion;
        const response = await services.client.command<{ result?: { file_id?: unknown } } | null>(action.command_name, input);
        const fileId = response?.result?.file_id;
        if (action.subject !== undefined && typeof fileId === 'string') await openDownload(fileId);
        onDone();
      } else if (action.kind === 'start_workflow' && action.workflow_id !== undefined) {
        const payload: Record<string, unknown> = { workflow_id: action.workflow_id };
        if (target !== null) payload['subject'] = { type: target.type, id: target.id };
        await services.client.command('StartWorkflow', { payload });
        onDone();
      }
    } catch (caught) {
      setError(caught instanceof ApiError ? { category: caught.category, message: caught.message } : { category: 'INTERNAL_ERROR', message: String(caught) });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="ay-actionbar" role="toolbar" aria-label="Actions">
      {relevant.map((action) => {
        const available = action.kind === 'command' || action.kind === 'open_view' || action.kind === 'start_workflow';
        const reason = unavailable(action);
        const hintId = `action-hint-${action.action_id}`;
        return (
          <span key={action.action_id} className="ay-actionbar__item">
            <button
              type="button"
              className={`ay-button${action.primary === true ? '' : ' ay-button--secondary'}`}
              disabled={!available || reason !== null || busy !== null}
              aria-busy={busy === action.action_id}
              aria-describedby={reason === null ? undefined : hintId}
              title={available ? undefined : 'Indisponible dans cette version'}
              onClick={() => void run(action)}
            >
              {text(action.label)}
            </button>
            {reason !== null && (
              <span id={hintId} className="ay-actionbar__hint">
                {reason}
              </span>
            )}
          </span>
        );
      })}
      {error !== null && (
        <p className={`ay-banner ${error.category === 'AUTHORIZATION_ERROR' || error.category === 'POLICY_REJECTION' ? 'ay-banner--warning' : 'ay-banner--danger'}`} role="alert">
          {error.category === 'AUTHORIZATION_ERROR' || error.category === 'POLICY_REJECTION' ? `Action refusée : ${error.message}` : error.message}
        </p>
      )}
      {pending !== null && (
        <CommandDialog
          command={pending.command}
          prefilled={pending.prefilled}
          target={pending.target}
          {...(pending.expectedVersion === undefined ? {} : { expectedVersion: pending.expectedVersion })}
          {...(pending.subject === undefined ? {} : { subject: pending.subject })}
          onClose={() => setPending(null)}
          onDone={() => {
            setPending(null);
            onDone();
          }}
        />
      )}
    </div>
  );
}
